import { Injectable, inject } from '@angular/core';
import { HttpRequestService } from '../http-request/http-request.service';
import { API, SUPERSET_URL } from '../../constants/api-url';
import { lastValueFrom, map, Observable, shareReplay, timer } from 'rxjs';
import { embedDashboard, EmbeddedDashboard, UiConfigType } from '@superset-ui/embedded-sdk';
import { AuthService } from '../auth/auth.service';
import { Store } from '@ngxs/store';
import { AuthState } from '../../store/auth/auth.state';

@Injectable({
  providedIn: 'root'
})
export class SuperSetService {
  private httpRequestService = inject(HttpRequestService);
  private authService = inject(AuthService);
  private store = inject(Store);
  private CHART_WITHOUT_ID = 'default';

  chartTokenCache = new Map<string, Observable<string>>();
  private readonly embeddedByMountId = new Map<string, EmbeddedDashboard>();

  getDefaultUiConfigType(): UiConfigType {
    return {
      hideTitle: true,
      hideTab: true,
      hideChartControls: false,
      filters: {
        visible: true,
        expanded: false
      },
      urlParams: {
        isSuperAdmin: this.authService.isSuperAdmin(),
        isAdmin: this.authService.isAdmin(),
        userType: this.authService.currentUserType(),
        currentUserId: this.store.selectSnapshot(AuthState.userId),
      }
    };
  }

  fetchEmbeddedToken(chartId: string = this.CHART_WITHOUT_ID): Observable<string> {
    if (!chartId) chartId = this.CHART_WITHOUT_ID;
    if (!this.chartTokenCache.has(chartId)) {
      const tokenRequest$ = this.httpRequestService.get(chartId === this.CHART_WITHOUT_ID ? API.SUPERSET.GUEST_TOKEN : `${API.SUPERSET.GUEST_TOKEN_FOR_CHART}?chartId=${chartId}`).pipe(
        map((res: { token: string }) => res.token),
        shareReplay(1)
      );
      this.chartTokenCache.set(chartId, tokenRequest$);
    }
    return this.chartTokenCache.get(chartId)!;
  }

  /**
   * Bounded wait until embedded UI is likely interactive. `getChartStates` may hang or stay empty on some
   * Superset / guest-token setups, so each RPC is time-limited and we fall back to scroll-size stability.
   */
  async waitForEmbeddedContentReady(
    embedded: EmbeddedDashboard,
    options?: { pollMs?: number; maxWaitMs?: number; rpcTimeoutMs?: number; minBeforeScrollFallbackMs?: number }
  ): Promise<'chartStates' | 'scrollStable' | 'timeout'> {
    const pollMs = options?.pollMs ?? 250;
    const maxWaitMs = options?.maxWaitMs ?? 20_000;
    const rpcTimeoutMs = options?.rpcTimeoutMs ?? 3_500;
    /** Avoid showing chrome while the embed often still paints (original issue). */
    const minBeforeScrollFallbackMs = options?.minBeforeScrollFallbackMs ?? 2_000;
    const stableNeeded = 3;
    const t0 = Date.now();
    let lastHeight: number | undefined;
    let stablePasses = 0;

    while (Date.now() - t0 < maxWaitMs) {
      const states = await this.racePromise(embedded.getChartStates(), rpcTimeoutMs);
      if (states && typeof states === 'object' && Object.keys(states as object).length > 0)
        return 'chartStates';

      const elapsed = Date.now() - t0;
      if (elapsed >= minBeforeScrollFallbackMs) {
        const size = await this.racePromise(embedded.getScrollSize(), rpcTimeoutMs);
        const h = size?.height ?? 0;
        if (h >= 200) {
          if (lastHeight !== undefined && Math.abs(h - lastHeight) < 4) {
            stablePasses++;
            if (stablePasses >= stableNeeded)
              return 'scrollStable';
          } else
            stablePasses = 0;
          lastHeight = h;
        } else {
          stablePasses = 0;
          lastHeight = undefined;
        }
      }
      await lastValueFrom(timer(pollMs));
    }
    return 'timeout';
  }

  registerEmbedded(mountId: string, embedded: EmbeddedDashboard): void {
    if (mountId)
      this.embeddedByMountId.set(mountId, embedded);
  }

  unregisterEmbedded(mountId: string): void {
    this.embeddedByMountId.delete(mountId);
  }

  getEmbedded(mountId: string): EmbeddedDashboard | undefined {
    return this.embeddedByMountId.get(mountId);
  }

  async getContentScrollHeight(embedded: EmbeddedDashboard, rpcTimeoutMs = 3_500): Promise<number> {
    const size = await this.racePromise(embedded.getScrollSize(), rpcTimeoutMs);
    return size?.height ?? 0;
  }

  /** Prevents a stuck host↔iframe RPC from blocking readiness forever. */
  private racePromise<T>(promise: Promise<T>, ms: number): Promise<T | undefined> {
    return new Promise<T | undefined>((resolve) => {
      const id = window.setTimeout(() => resolve(undefined), ms);
      promise.then((v) => {
        window.clearTimeout(id);
        resolve(v);
      }).catch(() => {
        window.clearTimeout(id);
        resolve(undefined);
      });
    });
  }

  async embed(chartId: string, element: HTMLElement, guestToken: string, dashboardUiConfig?: UiConfigType): Promise<EmbeddedDashboard> {
    try {
      if (!dashboardUiConfig)
        dashboardUiConfig = this.getDefaultUiConfigType();
      const embedded = await embedDashboard({
        id: chartId,
        supersetDomain: SUPERSET_URL,
        mountPoint: element,
        fetchGuestToken: () => Promise.resolve(guestToken),
        dashboardUiConfig: dashboardUiConfig
      });
      if (element.id)
        this.registerEmbedded(element.id, embedded);
      return embedded;
    } catch (error) {
      console.error(`Failed to embed chart ${chartId}:`, error);
      throw error;
    }
  }

  async syncEmbeddedIframeHeight(embedded: EmbeddedDashboard, mountPoint: HTMLElement, options?: { minHeightPx?: number; retries?: number; delayMs?: number }): Promise<void> {
    const minHeightPx = options?.minHeightPx ?? 320;
    const retries = options?.retries ?? 4;
    const delayMs = options?.delayMs ?? 160;

    for (let attempt = 0; attempt < retries; attempt++) {
      const size = await this.racePromise(embedded.getScrollSize(), 3_500);
      const iframe = mountPoint.querySelector('iframe');
      const contentHeight = size?.height ?? 0;

      if (iframe && contentHeight > 0) {
        const finalHeight = Math.max(minHeightPx, Math.ceil(contentHeight));
        iframe.style.transition = 'height 180ms ease';
        iframe.style.height = `${finalHeight}px`;
        iframe.style.minHeight = `${finalHeight}px`;
        iframe.style.width = '100%';
        iframe.style.display = 'block';
        mountPoint.style.transition = 'min-height 180ms ease';
        mountPoint.style.height = 'auto';
        mountPoint.style.minHeight = `${finalHeight}px`;
        return;
      }
      await lastValueFrom(timer(delayMs));
    }
  }
}
